import { useSelector } from 'react-redux';
import { WorksheetAssociatedTestSection, WorksheetEquipmentDropdown } from '../../Core';
import { Field, RadioGroup, NumericInput, TextOutput } from '../../../Core';
import { ListFieldSection } from '../../../Layout';
import { withInjectedProps, NamePrefixModifier } from '../../../Core/InjectedPropsContext';
import deepEquals from '../../../../Lib/Object/deepEquals';
import ids from '../../QestIds';
import externalTestMethods from '../../ExternalTestMethods';
import { WorksheetRootComponent } from 'Components/Worksheets/Core/types';
import { MaterialMassesSection } from './Sections';
import { DryMassesDetermination } from '../types';
import { WorksheetBalanceFieldSet } from '../../Core/WorksheetEquipment';

const qestIds = [ids.oversizeDetermination.AASHTO_T99_T180];

let MeasuredOversizeDeterminationWorksheet: WorksheetRootComponent = ({ createWorksheetSelector }) => {
  const dryMassesDetermination = useSelector(createWorksheetSelector!('DryMassesDetermination')) as
    | DryMassesDetermination
    | undefined;

  // Obtain data from the parent worksheet, assumed to be an MDD.
  // TODO: The Oversize Determination should have all of the information it needs to be performed
  // within its model
  const oversizeSieveSize = useSelector(
    createWorksheetSelector!('data.Preparation.ScreeningASTM.OversizeSieve', false),
    deepEquals
  );
  const importedFineMoistureTestId = useSelector(
    createWorksheetSelector!('data.Preparation.AssociatedFineMoisture.ImportedTest.Id', false)
  );
  const importedOversizeMoistureTestId = useSelector(
    createWorksheetSelector!('data.Preparation.AssociatedOversizeMoisture.ImportedTest.Id', false)
  );

  return (
    <>
      <ListFieldSection>
        <WorksheetBalanceFieldSet emph label="Oversize Balance" name="Equipment.Balance" />
      </ListFieldSection>
      <ListFieldSection>
        <Field input={<TextOutput />} label="Oversize Sieve Size" value={oversizeSieveSize.Caption} />
        <Field
          emph
          input={<WorksheetEquipmentDropdown />}
          label="Oversize Sieve"
          equipmentType="sieve"
          name="Sieves[0].Sieve"
          queryParams={{ SieveSize: `${oversizeSieveSize?.Size.value} ${oversizeSieveSize?.Size.uom}` }}
        />
      </ListFieldSection>
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          label="Dry Masses Determined By"
          name="DryMassesDetermination"
          options={[
            { label: 'Already dry', value: 'already_dry' },
            { label: 'Direct drying', value: 'direct_drying' },
            { label: 'Coarse and fine material moisture content', value: 'coarse_fine_moisture_content' },
          ]}
        />
      </ListFieldSection>
      <NamePrefixModifier appendNamePrefix="CoarseMaterial">
        <MaterialMassesSection heading="Coarse Material" dryMassesDetermination={dryMassesDetermination} />
      </NamePrefixModifier>
      {dryMassesDetermination === 'coarse_fine_moisture_content' && (
        <NamePrefixModifier namePrefix="data">
          <WorksheetAssociatedTestSection
            heading="Coarse Material Moisture Content"
            label="Coarse Material Moisture Content Determined by:"
            name="Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture"
            testType="Moisture Content"
            associatedTestModel={{
              measured: true,
              imported: {
                importedTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<TextOutput />}
                      label="Moisture Content (%)"
                      name="Preparation.ScreeningASTM.OversizeMoistureContent"
                      emph
                    />
                  </ListFieldSection>
                ),
                importModel: {
                  Id: importedOversizeMoistureTestId,
                },
              },
              external: {
                testMethods: [externalTestMethods.moistureContent.AASHTO_T_265],
                permitOtherTestMethods: true,
                externalTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<NumericInput />}
                      label="Moisture Content (%)"
                      name="Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture.ExternalTest.Results.MoistureContentPercent"
                      emph
                    />
                  </ListFieldSection>
                ),
              },
            }}
          />
        </NamePrefixModifier>
      )}
      <NamePrefixModifier appendNamePrefix="PassingMaterial">
        <MaterialMassesSection heading="Passing Material" dryMassesDetermination={dryMassesDetermination} />
      </NamePrefixModifier>
      {dryMassesDetermination === 'coarse_fine_moisture_content' && (
        <NamePrefixModifier namePrefix="data">
          <WorksheetAssociatedTestSection
            heading="Passing Material Moisture Content"
            label="Passing Material Moisture Content Determined By:"
            name="Preparation.AssociatedFineMoisture"
            testType="Moisture Content"
            associatedTestModel={{
              measured: true,
              imported: {
                importedTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<TextOutput />}
                      label="Passing Material Moisture Content (%)"
                      name="Preparation.FineMoistureContent"
                      emph
                    />
                  </ListFieldSection>
                ),
                importModel: {
                  Id: importedFineMoistureTestId,
                },
              },
              external: {
                testMethods: [externalTestMethods.moistureContent.AASHTO_T_265],
                permitOtherTestMethods: false,
                externalTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<NumericInput />}
                      label="Passing Material Moisture Content (%)"
                      name="Preparation.AssociatedFineMoisture.ExternalTest.Results.MoistureContentPercent"
                    />
                  </ListFieldSection>
                ),
              },
            }}
          />
        </NamePrefixModifier>
      )}
    </>
  );
};

MeasuredOversizeDeterminationWorksheet = withInjectedProps(MeasuredOversizeDeterminationWorksheet);

MeasuredOversizeDeterminationWorksheet.qestIds = qestIds;

export default MeasuredOversizeDeterminationWorksheet;
export { MeasuredOversizeDeterminationWorksheet };
